import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import BackendAPI from '../lib/api';

export type ImportJobStatus = 'queued' | 'processing' | 'ready' | 'failed';

export interface ImportJob {
  job_id: string;
  filename: string;
  status: ImportJobStatus;
  started_at: number; // epoch ms — drives the dashboard elapsed timer
  parse_hash: string;
  project_id?: string | null;
  error?: string | null;
}

interface ImportJobsState {
  jobs: ImportJob[];
  addJob: (_job: ImportJob) => void;
  removeJob: (_jobId: string) => void;
  removeJobsByProject: (_projectId: string) => void;
  /** B-601: drop any Processing entry for an import that was just opened. */
  removeJobsByParseHash: (_parseHash: string) => void;
  updateJob: (_jobId: string, _patch: Partial<ImportJob>) => void;
  startPolling: () => void;
  stopPolling: () => void;
  /** B-509: stop polling and drop every job. Called on logout / user-switch so
   * one account's in-progress imports never show in the next on a shared
   * browser (the persisted `aiea-import-jobs` is overwritten empty). */
  clearAll: () => void;
}

// Module-scoped interval handle (not part of persisted state).
let _interval: ReturnType<typeof setInterval> | null = null;
const TERMINAL: ImportJobStatus[] = ['ready', 'failed'];

const useImportJobsStore = create<ImportJobsState>()(
  persist(
    (set, get) => ({
      jobs: [],
      addJob: (job) =>
        set((s) => ({ jobs: [job, ...s.jobs.filter((j) => j.job_id !== job.job_id)] })),
      removeJob: (jobId) => set((s) => ({ jobs: s.jobs.filter((j) => j.job_id !== jobId) })),
      removeJobsByParseHash: (parseHash) =>
        set((s) => ({ jobs: s.jobs.filter((j) => j.parse_hash !== parseHash) })),
      // B-502: when a project is deleted, its imports cascade-delete on the
      // backend; drop any Processing job that belonged to it so the pane doesn't
      // strand a row for a job whose import no longer exists.
      removeJobsByProject: (projectId) =>
        set((s) => ({ jobs: s.jobs.filter((j) => j.project_id !== projectId) })),
      updateJob: (jobId, patch) =>
        set((s) => ({
          jobs: s.jobs.map((j) => (j.job_id === jobId ? { ...j, ...patch } : j)),
        })),
      // B-509: full wipe on logout / user-switch — stop the poller and clear
      // every job so the next user on this browser starts clean.
      clearAll: () => {
        get().stopPolling();
        set({ jobs: [] });
      },
      startPolling: () => {
        if (_interval) return;
        const tick = async () => {
          const active = get().jobs.filter((j) => !TERMINAL.includes(j.status));
          if (active.length === 0) {
            get().stopPolling();
            return;
          }
          await Promise.all(
            active.map(async (j) => {
              try {
                const res = await BackendAPI.getJobStatus(j.job_id);
                if (res.status === 'ready') {
                  get().updateJob(j.job_id, {
                    status: 'ready',
                    parse_hash: res.parse_hash ?? j.parse_hash,
                  });
                } else if (res.status === 'failed') {
                  get().updateJob(j.job_id, {
                    status: 'failed',
                    error: res.error || 'Import failed.',
                  });
                } else {
                  get().updateJob(j.job_id, { status: res.status });
                }
              } catch {
                /* transient — keep polling on the next tick */
              }
            }),
          );
        };
        _interval = setInterval(tick, 2500);
        void tick();
      },
      stopPolling: () => {
        if (_interval) {
          clearInterval(_interval);
          _interval = null;
        }
      },
    }),
    {
      name: 'aiea-import-jobs',
      partialize: (s) => ({ jobs: s.jobs }),
      // Resume polling for any non-terminal jobs after a reload/navigation.
      onRehydrateStorage: () => (state) => {
        if (state && state.jobs.some((j) => !TERMINAL.includes(j.status)))
          state.startPolling();
      },
    },
  ),
);

export default useImportJobsStore;
